import React, { useState, useEffect, useRef } from "react";
import QRCode from "react-qr-code";
import AppBar from "./AppBar";
import MemberStrip from "./MemberStrip";
import { useMandate } from "./MandateContext";
import customFetch from "../apicall/api";

const POLL_INTERVAL = 5000; // 5 seconds

function fmtDate(d) {
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export default function MandateForm({ onNext, onBack, api_token }) {
  const { state, update } = useMandate();
  api_token = api_token || localStorage.getItem("api_token");

  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");
  const [registered, setRegistered] = useState(!!state.redirectUrl);  // true if QR already generated (re-entry)
  const [pollState,  setPollState]  = useState("idle");  // idle | waiting | checking | done

  const pollRef = useRef(null);

  // ── on mount: restore QR from vpaDetails if context is empty (e.g. fresh page load) ─
  useEffect(() => {
    if (registered) return; // QR already in context from same-session re-entry
    const vpaDetails = (state.selectedMember && state.selectedMember.vpaDetails) || [];
    const initiated = vpaDetails.find(
      (v) => v.autopay_status_type_flag === "I" && v.redirect_url
    );
    if (initiated) {
      update({
        redirectUrl:      initiated.redirect_url                    || "",
        redirectType:     initiated.redirect_type                   || "",
        merchantUserId:   initiated.third_reference_number_nach     || "",
        authRequestId:    initiated.auth_request_id                 || "",
        mandateRef:       initiated.second_reference_number_nach    || "",
        maxBillingAmount: initiated.max_billing_amount || state.maxBillingAmount,
      });
      setRegistered(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function stopPoll() {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }

  // Start polling once QR is registered
  useEffect(() => {
    if (!registered || !state.merchantUserId) return;

    setPollState("waiting");
    pollRef.current = setInterval(() => {
      setPollState("checking");
      const qp = new URLSearchParams({ merchant_user_id: state.merchantUserId }).toString();
      customFetch(
        process.env.REACT_APP_URL + "/upi/get/auth/request/status?" + qp,
        { method: "GET", headers: { "api-token": api_token } }
      )
        .then((res) => res.json())
        .then((data) => {
          if (data.api_code === 4) { localStorage.clear(); window.location.href = "/"; return; }
          const resp = Array.isArray(data) ? data[0] : data;
          const txState        = resp && resp.data && resp.data.transactionDetails && resp.data.transactionDetails.state;
          const payRespCode   = resp && resp.data && resp.data.transactionDetails && resp.data.transactionDetails.payResponseCode;
          if (txState === "COMPLETED") {
            stopPoll();
            update({ status: "success" });
            setPollState("done");
            onNext();
          } else if (txState === "FAILED") {
            stopPoll();
            const isExpiry = payRespCode === "INTENT_EXPIRED" || payRespCode === "SUBSCRIPTION_EXPIRED";
            if (isExpiry) {
              update({ redirectUrl: "", redirectType: "", merchantUserId: "", authRequestId: "", mandateRef: "" });
              setRegistered(false);
              setPollState("idle");
              setError("QR code expired. Please generate a new one.");
            } else {
              update({ status: "failed" });
              setPollState("done");
              onNext();
            }
          } else {
            setPollState("waiting");
          }
        })
        .catch(() => setPollState("waiting"));
    }, POLL_INTERVAL);

    return () => stopPoll();
  }, [registered, state.merchantUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  const memberName = (state.selectedMember && state.selectedMember.memberName) || "";
  const maxAmount  = state.maxBillingAmount || 0;
  const today      = new Date();
  const endDate    = new Date(today);
  endDate.setFullYear(endDate.getFullYear() + 1);

  function registerMandate() {
    if (loading) return;
    setLoading(true);
    setError("");

    const formdata = new FormData();
    formdata.append("household_id",     state.householdId);
    formdata.append("family_member_id", state.selectedMember && state.selectedMember.family_member_id);

    customFetch(process.env.REACT_APP_URL + "/upi/register/autopay", {
      method: "POST",
      headers: { "api-token": api_token },
      body: formdata,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.api_code === 4) { localStorage.clear(); window.location.href = "/"; return; }
        const resp = Array.isArray(data) ? data[0] : data;
        if (!resp || resp.status !== 1) {
          setError(resp.message || "Registration failed. Please try again.");
          setLoading(false);
          return;
        }
        // Mandate already REGISTERED — skip QR, navigate to confirmation
        if (resp.already_registered) {
          update({
            status: "success",
            mandateRef:    resp.subscription_id   || "",
            merchantUserId: resp.merchant_user_id || "",
          });
          setLoading(false);
          onNext();
          return;
        }
        update({
          redirectType:     resp.redirect_type   || "",
          redirectUrl:      resp.redirect_url    || "",
          merchantUserId:   resp.merchant_user_id || "",
          authRequestId:    resp.auth_request_id  || "",
          mandateRef:       resp.subscription_id  || "",
          maxBillingAmount: resp.max_billing_amount || state.maxBillingAmount,
        });
        setRegistered(true);
        setLoading(false);
      })
      .catch(() => {
        setError("Network error. Please try again.");
        setLoading(false);
      });
  }

  const qrValue    = state.redirectUrl || "";
  const isBase64QR = qrValue.startsWith("data:image");

  return (
    <section className="main-sec" style={{ minHeight: "100vh" }}>
      <div className="container fix-width">
        <AppBar title="Mandate Details" onBack={onBack} />
        <MemberStrip memberName={memberName} />

        <div style={{ padding: "20px 0 20px" }}>

          {/* QR generation */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "#374151", letterSpacing: "0.05em", marginBottom: 6 }}>
              Generate QR for Mandate Authorisation
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12 }}>
              The customer scans this QR code with any UPI app to approve the mandate.
            </div>

            {!registered && (
              <button
                style={{ background: "#0178B7", color: "#fff", borderRadius: 8, border: "none", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", fontWeight: 700, marginBottom: 12, padding: "12px 0", cursor: loading ? "not-allowed" : "pointer" }}
                disabled={loading}
                onClick={registerMandate}
              >
                {loading
                  ? <><span style={{ display: "inline-block", width: 14, height: 14, border: "2px solid #fff", borderRightColor: "transparent", borderRadius: "50%", animation: "upi-spin 0.75s linear infinite", marginRight: 8 }} />Generating…</>
                  : <><span style={{ fontSize: 16 }}>⊞</span> Generate QR Code</>}
              </button>
            )}

            {error && (
              <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#dc2626", marginBottom: 10 }}>
                {error}
                <button style={{ background: "#dc2626", color: "#fff", borderRadius: 6, border: "none", padding: "2px 8px", marginLeft: 8, cursor: "pointer", fontSize: 12 }} onClick={registerMandate}>Retry</button>
              </div>
            )}

            {registered && qrValue && (
              <div style={{ textAlign: "center", marginBottom: 14 }}>
                <div style={{ display: "inline-block", padding: 12, background: "#fff", border: "2px solid #0178B7", borderRadius: 12, marginBottom: 8 }}>
                  {isBase64QR
                    ? <img src={qrValue} alt="UPI Mandate QR" style={{ width: 180, height: 180, display: "block" }} />
                    : <QRCode value={qrValue} size={180} />}
                </div>
                <div style={{ fontSize: 12, color: "#166534", marginTop: 4, display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                  <span>✅</span> QR code generated successfully
                </div>
                {/* Polling status */}
                <div style={{ marginTop: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontSize: 12, color: "#6b7280" }}>
                  {pollState === "checking"
                    ? <><span style={{ display: "inline-block", width: 12, height: 12, border: "2px solid #0178B7", borderRightColor: "transparent", borderRadius: "50%", animation: "upi-spin 0.75s linear infinite" }} /> Checking status…</>
                    : <><span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: "#d97706" }} /> Waiting for customer approval…</>}
                </div>
                <button
                  style={{ background: "transparent", color: "#0178B7", border: "1.5px solid #0178B7", borderRadius: 20, fontSize: 12, padding: "4px 12px", cursor: "pointer", marginTop: 8 }}
                  onClick={() => { stopPoll(); setRegistered(false); setPollState("idle"); registerMandate(); }}
                >
                  Regenerate QR
                </button>
              </div>
            )}

            <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: "10px 12px", marginBottom: 14 }}>
              <div style={{ display: "flex", gap: 8 }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>ℹ️</span>
                <div style={{ fontSize: 12, color: "#1e40af" }}>
                  Ask the customer to open any UPI app, scan this QR, and enter their UPI PIN to approve the recurring mandate.
                </div>
              </div>
            </div>
          </div>
        </div>

        <style>{`
          @keyframes upi-spin {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </section>
  );
}
